// ... existing code ...
import { useNavigate, Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import VIPProductGrid from "./components/VIPProductGrid";
import VIPUpgradePrompt from "./components/VIPUpgradePrompt";
import { useAuth } from "../../hooks/useAuth";
import { useProducts } from "../../hooks/useProducts";
import { canAccessVIP } from "@/lib/membership";

export default function VIPSubscribe() {
  const navigate = useNavigate();
  const { profile, isLoggedIn, loading: authLoading } = useAuth();
  const isVIP = canAccessVIP(profile?.membership_level);

  const { products, loading, error } = useProducts("vip");

  const handleOrder = (productId: string) => {
    navigate(`/product/${productId}`);
  };

  return (
    <div className="min-h-screen bg-stone-950">
      <Navbar />

      <main className="pt-20 md:pt-24">
        {/* Dark Hero - Premium */}
        <section className="relative overflow-hidden">
          {/* Background image */}
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=dark%20luxurious%20abstract%20background%20with%20subtle%20golden%20amber%20light%20rays%20and%20soft%20bokeh%20particles%2C%20deep%20black%20and%20charcoal%20gradient%2C%20premium%20vip%20aesthetic%2C%20elegant%20minimal%20design%20with%20warm%20glow%20highlights%2C%20cinematic%20atmosphere%2C%20no%20text%2C%20high%20contrast%20dark%20theme&width=1920&height=600&seq=vip-hero-bg&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-stone-950/70" />
            <div className="absolute inset-0 bg-gradient-to-b from-stone-950/40 via-transparent to-stone-950" />
          </div>

          {/* Animated floating particles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {Array.from({ length: 20 }).map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full bg-amber-400/20 animate-pulse"
                style={{
                  width: `${Math.random() * 3 + 1}px`,
                  height: `${Math.random() * 3 + 1}px`,
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDuration: `${Math.random() * 4 + 3}s`,
                  animationDelay: `${Math.random() * 3}s`,
                }}
              />
            ))}
          </div>

          {/* Large ambient glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-amber-500/8 rounded-full blur-[140px]" />
          <div className="absolute top-0 right-1/4 w-[300px] h-[300px] bg-amber-600/5 rounded-full blur-[100px]" />

          <div className="relative py-20 md:py-28 px-4 md:px-8 lg:px-12">
            <div className="max-w-3xl mx-auto text-center">
              {/* Crown badge with glow */}
              <div className="inline-flex items-center gap-2.5 px-5 py-2 rounded-full bg-amber-500/10 border border-amber-500/25 text-amber-400 mb-8 backdrop-blur-sm shadow-[0_0_30px_rgba(245,158,11,0.15)]">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-vip-crown-fill text-sm" />
                </div>
                <span className="font-body text-xs font-semibold tracking-wide">尊榮會員專區</span>
              </div>

              {/* Main title with subtle letter spacing */}
              <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-5 tracking-tight leading-tight">
                VIP 認購區
              </h1>

              {/* Decorative line */}
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="w-12 h-px bg-gradient-to-r from-transparent to-amber-500/50" />
                <div className="w-1.5 h-1.5 rounded-full bg-amber-500/60" />
                <div className="w-12 h-px bg-gradient-to-l from-transparent to-amber-500/50" />
              </div>

              <p className="font-body text-sm md:text-base text-stone-400 leading-relaxed max-w-lg mx-auto mb-10">
                僅限 VIP會員或以上等級下單。珍稀限量貨源，高利潤空間，為追求卓越回報的你精心甄選。
              </p>

              {/* Stats row */}
              <div className="flex items-center justify-center gap-8 md:gap-12">
                <div className="text-center">
                  <div className="font-display text-2xl md:text-3xl font-bold text-amber-400 mb-1">25%</div>
                  <div className="font-body text-xs text-stone-500">最高利潤率</div>
                </div>
                <div className="w-px h-10 bg-stone-700/50" />
                <div className="text-center">
                  <div className="font-display text-2xl md:text-3xl font-bold text-white mb-1">500+</div>
                  <div className="font-body text-xs text-stone-500">珍稀貨源</div>
                </div>
                <div className="w-px h-10 bg-stone-700/50" />
                <div className="text-center">
                  <div className="font-display text-2xl md:text-3xl font-bold text-white mb-1">VIP</div>
                  <div className="font-body text-xs text-stone-500">專屬通道</div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom fade to content */}
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-stone-950 to-transparent" />
        </section>

        {/* Upgrade prompt for non-VIP logged-in users */}
        {isLoggedIn && !isVIP && !authLoading && (
          <section className="py-8 md:py-10 px-4 md:px-8 lg:px-12">
            <VIPUpgradePrompt />
          </section>
        )}

        {/* Product Grid Section */}
        <section className="py-12 md:py-16 px-4 md:px-8 lg:px-12">
          <div className="max-w-6xl mx-auto">
            {/* Section header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 md:mb-10">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-vip-diamond-line text-amber-400" />
                  <h2 className="font-display text-xl md:text-2xl font-bold text-white">
                    VIP 限定貨源
                  </h2>
                </div>
                <p className="font-body text-xs md:text-sm text-stone-500">
                  共 {products.length} 件珍稀貨源開放認購，認購價與銷售價已解鎖
                </p>
              </div>
              <Link
                to="/subscribe-trial"
                className="inline-flex items-center gap-2 font-body text-xs md:text-sm font-semibold px-4 py-2.5 rounded-full border border-stone-700 text-stone-400 hover:border-rose-400 hover:text-rose-400 transition-colors whitespace-nowrap self-start sm:self-auto"
              >
                <i className="ri-sparkling-line" />
                前往體驗區
              </Link>
            </div>

            {error && (
              <div className="mb-4 font-body text-sm px-4 py-3 rounded-lg bg-red-50 text-red-600 border border-red-200">
                {error}
              </div>
            )}

            {loading && products.length === 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="bg-stone-800 rounded-2xl border border-stone-700 overflow-hidden">
                    <div className="w-full h-48 md:h-56 bg-stone-700 animate-pulse" />
                    <div className="p-4 md:p-5 space-y-3">
                      <div className="h-5 bg-stone-700 rounded animate-pulse w-3/4" />
                      <div className="h-4 bg-stone-700 rounded animate-pulse w-full" />
                      <div className="h-4 bg-stone-700 rounded animate-pulse w-2/3" />
                      <div className="h-8 bg-stone-700 rounded animate-pulse w-full mt-2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <VIPProductGrid products={products} isVIP={isVIP} onOrder={handleOrder} />
            )}
          </div>
        </section>

        {/* Info Banner */}
        <section className="py-10 md:py-14 px-4 md:px-8 lg:px-12 border-t border-stone-800">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
                  <i className="ri-lock-star-line text-amber-400 text-lg" />
                </div>
                <div>
                  <h3 className="font-display text-sm font-semibold text-white mb-1">限量珍稀</h3>
                  <p className="font-body text-xs text-stone-500 leading-relaxed">每件 VIP 貨源均為限量發行，全球配額有限，確保獨特性與利潤空間。</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-rose-800/20 flex items-center justify-center shrink-0">
                  <i className="ri-line-chart-line text-rose-400 text-lg" />
                </div>
                <div>
                  <h3 className="font-display text-sm font-semibold text-white mb-1">高回報率</h3>
                  <p className="font-body text-xs text-stone-500 leading-relaxed">專業團隊嚴選貨源，利潤空間 20%-25%，遠超市面平均。</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                  <i className="ri-shield-star-line text-emerald-400 text-lg" />
                </div>
                <div>
                  <h3 className="font-display text-sm font-semibold text-white mb-1">安全保障</h3>
                  <p className="font-body text-xs text-stone-500 leading-relaxed">所有貨源附正品證書，60 天無條件退換，VIP 專屬管家全程跟進。</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}